/**
 * Hook useNewOrderAlert
 *
 * Alerta global de novos pedidos – funciona em qualquer página do sistema.
 *
 * Escuta eventos WebSocket `order:created` e `order:status` para:
 * - Detectar novos pedidos (PENDING_PAYMENT)
 * - Detectar quando pedidos são aceitos/recusados (remove do alerta)
 *
 * Toca um sino intermitente via Web Audio API até que todos os pedidos
 * sejam aceitos ou recusados.
 *
 * Busca dados completos do pedido via API para exibição no painel.
 *
 * NÃO depende do hook useOrders nem do array de pedidos da página.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSocketEvent } from './useWebSocket';
import { getOrder } from '@services/ordersService';
import type { Order } from '@/types/order';

/**
 * Toca um único "ding" de sineta de balcão/recepção.
 *
 * Simula o som metálico e agudo de uma campainha de serviço:
 * - Fundamental aguda (~3200 Hz) com ataque rápido
 * - Harmônicos inarmônicos (típicos de metal) para timbre metálico
 * - Decaimento rápido (~0.6s) como uma sineta real
 */
function playDeskBellStrike(ctx: AudioContext, startTime: number, volume: number): void {
  // Fundamental - tom principal da sineta (agudo e claro)
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = 'sine';
  osc1.frequency.setValueAtTime(3200, startTime);
  gain1.gain.setValueAtTime(volume, startTime);
  gain1.gain.exponentialRampToValueAtTime(0.001, startTime + 0.6);
  osc1.connect(gain1);
  gain1.connect(ctx.destination);
  osc1.start(startTime);
  osc1.stop(startTime + 0.7);

  // Harmônico 1 - frequência inarmônica (2.76x) para timbre metálico
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(8832, startTime);
  gain2.gain.setValueAtTime(volume * 0.25, startTime);
  gain2.gain.exponentialRampToValueAtTime(0.001, startTime + 0.3);
  osc2.connect(gain2);
  gain2.connect(ctx.destination);
  osc2.start(startTime);
  osc2.stop(startTime + 0.4);

  // Harmônico 2 - frequência inarmônica (5.4x) para brilho metálico
  const osc3 = ctx.createOscillator();
  const gain3 = ctx.createGain();
  osc3.type = 'sine';
  osc3.frequency.setValueAtTime(17280, startTime);
  gain3.gain.setValueAtTime(volume * 0.08, startTime);
  gain3.gain.exponentialRampToValueAtTime(0.001, startTime + 0.15);
  osc3.connect(gain3);
  gain3.connect(ctx.destination);
  osc3.start(startTime);
  osc3.stop(startTime + 0.2);

  // Sub-harmônico (corpo/ressonância do metal)
  const osc4 = ctx.createOscillator();
  const gain4 = ctx.createGain();
  osc4.type = 'sine';
  osc4.frequency.setValueAtTime(1600, startTime);
  gain4.gain.setValueAtTime(volume * 0.3, startTime);
  gain4.gain.exponentialRampToValueAtTime(0.001, startTime + 0.5);
  osc4.connect(gain4);
  gain4.connect(ctx.destination);
  osc4.start(startTime);
  osc4.stop(startTime + 0.6);

  // Ruído de impacto (simula a batida na sineta)
  const bufferSize = ctx.sampleRate * 0.02; // 20ms de ruído
  const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = noiseBuffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = (Math.random() * 2 - 1) * 0.5;
  }
  const noise = ctx.createBufferSource();
  const noiseGain = ctx.createGain();
  const noiseFilter = ctx.createBiquadFilter();
  noise.buffer = noiseBuffer;
  noiseFilter.type = 'bandpass';
  noiseFilter.frequency.setValueAtTime(4000, startTime);
  noiseFilter.Q.setValueAtTime(2, startTime);
  noiseGain.gain.setValueAtTime(volume * 0.4, startTime);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.03);
  noise.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(ctx.destination);
  noise.start(startTime);
  noise.stop(startTime + 0.05);
}

/**
 * Toca 3 toques rápidos de sineta de balcão: "ding! ding! ding!"
 * Como um atendente batendo na campainha de serviço 3 vezes.
 */
function playBellTone(ctx: AudioContext): void {
  const now = ctx.currentTime;
  // Três toques com intervalo de 0.35s entre eles
  playDeskBellStrike(ctx, now, 0.35);
  playDeskBellStrike(ctx, now + 0.35, 0.4);
  playDeskBellStrike(ctx, now + 0.7, 0.45);
}

/**
 * Hook global para alerta sonoro de novos pedidos.
 *
 * Escuta eventos WebSocket diretamente (não depende de useOrders).
 * Toca sino continuamente até que os pedidos sejam aceitos ou recusados.
 */
export function useNewOrderAlert() {
  const [alertingMap, setAlertingMap] = useState<Map<string, Order | null>>(new Map());
  const audioContextRef = useRef<AudioContext | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Ref para rastrear o tamanho do mapa e evitar re-renders desnecessários no efeito do sino
  const alertingSizeRef = useRef(0);

  /**
   * Toca o sino uma vez.
   * Aguarda resume do AudioContext antes de tocar.
   */
  const ringBell = useCallback(() => {
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (
          window.AudioContext || (window as any).webkitAudioContext
        )();
      }
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') {
        ctx.resume().then(() => {
          playBellTone(ctx);
        });
      } else {
        playBellTone(ctx);
      }
    } catch {
      // Silencioso - navegador pode bloquear áudio sem interação do usuário
    }
  }, []);

  /**
   * Inicia o sino intermitente.
   */
  const startBell = useCallback(() => {
    // Limpa intervalo anterior se existir
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    // Toca imediatamente
    ringBell();
    // Repete a cada 4 segundos
    intervalRef.current = setInterval(ringBell, 4000);
  }, [ringBell]);

  /**
   * Para o sino.
   */
  const stopBell = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /**
   * Controla o sino baseado no tamanho do mapa de alertas.
   */
  useEffect(() => {
    const currentSize = alertingMap.size;
    const prevSize = alertingSizeRef.current;
    alertingSizeRef.current = currentSize;

    if (currentSize > 0 && prevSize === 0) {
      // Primeiro pedido chegou - inicia sino
      startBell();
    } else if (currentSize > prevSize && currentSize > 0) {
      // Mais pedidos chegaram - reinicia sino para tocar imediatamente
      startBell();
    } else if (currentSize === 0) {
      // Todos os pedidos foram tratados - para sino
      stopBell();
    }

    // Cleanup: sempre para o sino quando o efeito re-executa ou desmonta
    return () => {
      stopBell();
    };
  }, [alertingMap.size, startBell, stopBell]);

  /**
   * Cleanup ao desmontar.
   */
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      audioContextRef.current?.close().catch(() => {});
    };
  }, []);

  /**
   * Busca dados completos do pedido via API e atualiza o mapa.
   */
  const fetchOrderDetails = useCallback(async (orderId: string) => {
    try {
      const order = await getOrder(orderId);
      setAlertingMap(prev => {
        // Só atualiza se o pedido ainda estiver no mapa (não foi removido entre request e response)
        if (!prev.has(orderId)) return prev;
        const next = new Map(prev);
        next.set(orderId, order);
        return next;
      });
    } catch {
      // Se falhar a busca, mantém o entry com null (dados parciais)
    }
  }, []);

  /**
   * Escuta evento de novo pedido via WebSocket.
   */
  const handleOrderCreated = useCallback(
    (event: { type: string; data: Record<string, unknown>; timestamp: string }) => {
      const orderData = event?.data;
      if (orderData?.id) {
        const status = orderData.status as string | undefined;
        if (!status || status === 'PENDING_PAYMENT') {
          const orderId = orderData.id as string;
          setAlertingMap(prev => {
            const next = new Map(prev);
            // Armazena temporariamente os dados parciais do WebSocket
            next.set(orderId, (orderData as unknown as Order) || null);
            return next;
          });
          // Busca dados completos via API
          fetchOrderDetails(orderId);
        }
      }
    },
    [fetchOrderDetails],
  );

  /**
   * Escuta evento de mudança de status via WebSocket.
   * Remove pedidos do alerta quando status muda de PENDING_PAYMENT.
   */
  const handleOrderStatus = useCallback(
    (event: { type: string; data: { orderId: string; status: string; order?: Record<string, unknown> }; timestamp: string }) => {
      const { orderId, status } = event?.data || {};
      if (!orderId) return;

      // Verifica usando o campo correto (backend envia currentStatus no order)
      const newStatus = status || (event.data.order as any)?.currentStatus;
      if (newStatus && newStatus !== 'PENDING_PAYMENT') {
        setAlertingMap(prev => {
          if (!prev.has(orderId)) return prev;
          const next = new Map(prev);
          next.delete(orderId);
          return next;
        });
      }
    },
    [],
  );

  useSocketEvent('order:created', handleOrderCreated);
  useSocketEvent('order:status', handleOrderStatus);

  /** Silencia todos os alertas */
  const dismissAll = useCallback(() => setAlertingMap(new Map()), []);

  /** Silencia um pedido específico */
  const dismissOrder = useCallback((orderId: string) => {
    setAlertingMap(prev => {
      const next = new Map(prev);
      next.delete(orderId);
      return next;
    });
  }, []);

  /** Restaura um pedido no alerta (rollback de remoção otimista em caso de erro na API) */
  const restoreOrder = useCallback((orderId: string, order: Order) => {
    setAlertingMap(prev => {
      const next = new Map(prev);
      next.set(orderId, order);
      return next;
    });
  }, []);

  /** Pedidos alertando (filtra nulls para segurança) */
  const alertingOrders = useMemo(
    () => Array.from(alertingMap.entries())
      .map(([id, order]) => order || ({ id } as Order))
      .filter(Boolean),
    [alertingMap],
  );

  return {
    hasAlertingOrders: alertingMap.size > 0,
    alertingCount: alertingMap.size,
    alertingOrders,
    dismissAll,
    dismissOrder,
    restoreOrder,
  };
}
